import type { InputHTMLAttributes, ReactNode, Ref, TextareaHTMLAttributes } from "react";
import { IconOstrzezenie } from "../icons/index.js";
import { Tooltip } from "./Tooltip.js";

/**
 * F-1: etykieta NAD polem, podpowiedz POD polem. Placeholder nigdy nie zastepuje
 * etykiety - znika po wpisaniu i pole traci nazwe.
 * F-12: blad pola to wlasny komunikat z ikona pod polem, nigdy natywny dymek
 * walidacji przegladarki (angielski, nieostylowany, poza naszym ukladem).
 * A-5: pole jest polaczone z etykieta (htmlFor) i z komunikatem (aria-describedby).
 */

/** Atrybuty, ktore Field wylicza za kontrolke - id, opis i sygnal bledu. */
export interface AtrybutyKontrolki {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
}

interface FieldProps {
  /** Widoczna etykieta pola. CP-4: rzeczownikowa, pisownia zdaniowa. */
  etykieta: string;
  /** Id kontrolki w srodku. Od niego pochodza id hintu i bledu. */
  id: string;
  /** P-4: podpowiedz pod polem (jednostka, limit, format). */
  hint?: string;
  /** F-12: komunikat bledu. Obecnosc = pole w stanie bledu. */
  blad?: string;
  /**
   * TT-4: wyjasnienie pod sama etykieta. Etykieta NIE dostaje zadnej dekoracji -
   * ani podkreslenia, ani ikony "?" - jedynym sygnalem jest kursor help.
   */
  tooltip?: string;
  /** F-4: limit szerokosci nalezy do POLA, nigdy do karty ani formularza. */
  waskie?: boolean;
  children: ReactNode | ((atrybuty: AtrybutyKontrolki) => ReactNode);
}

/** Opakowanie pojedynczego pola: etykieta, kontrolka, hint, blad. */
export function Field({ etykieta, id, hint, blad, tooltip, waskie = false, children }: FieldProps): React.JSX.Element {
  const idHintu = `${id}-hint`;
  const idBledu = `${id}-error`;
  const opisy = [hint ? idHintu : null, blad ? idBledu : null].filter(Boolean).join(" ");

  const atrybuty: AtrybutyKontrolki = {
    id,
    ...(opisy ? { "aria-describedby": opisy } : {}),
    ...(blad ? { "aria-invalid": true as const } : {}),
  };

  const etykietaEl = (
    <label className={`field-label${tooltip ? " field-label--hint" : ""}`} htmlFor={id}>
      {etykieta}
    </label>
  );

  return (
    <div className={`field${waskie ? " field--waskie" : ""}`}>
      {tooltip ? <Tooltip tekst={tooltip}>{etykietaEl}</Tooltip> : etykietaEl}
      {typeof children === "function" ? children(atrybuty) : children}
      {hint && (
        <span className="field-hint" id={idHintu}>
          {hint}
        </span>
      )}
      {blad && (
        <span className="field-error" id={idBledu}>
          <IconOstrzezenie size={14} />
          {blad}
        </span>
      )}
    </div>
  );
}

type WspolneProps = Pick<FieldProps, "etykieta" | "id" | "hint" | "blad" | "tooltip" | "waskie">;

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "className">, WspolneProps {
  ref?: Ref<HTMLInputElement>;
}

/**
 * F-5: pole na --surface-sunken z promieniem --radius-field i focus ringiem.
 * T-5 wprost NIE dotyczy pol formularza - szarosc odroznia tu kontrolke od karty
 * i zostaje.
 */
export function Input({ etykieta, id, hint, blad, tooltip, waskie, ref, ...reszta }: InputProps): React.JSX.Element {
  return (
    <Field etykieta={etykieta} id={id} hint={hint} blad={blad} tooltip={tooltip} waskie={waskie}>
      {(atrybuty) => <input {...atrybuty} {...reszta} ref={ref} className="input" />}
    </Field>
  );
}

interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "className">, WspolneProps {
  ref?: Ref<HTMLTextAreaElement>;
}

/** Ta sama powierzchnia co Input (F-5); F-2 dopuszcza dla textarea pelna szerokosc. */
export function Textarea({
  etykieta,
  id,
  hint,
  blad,
  tooltip,
  waskie,
  ref,
  ...reszta
}: TextareaProps): React.JSX.Element {
  return (
    <Field etykieta={etykieta} id={id} hint={hint} blad={blad} tooltip={tooltip} waskie={waskie}>
      {(atrybuty) => <textarea {...atrybuty} {...reszta} ref={ref} className="input input--wieloliniowy" />}
    </Field>
  );
}
